import styled, { useTheme } from "styled-components/native";
import React, { useCallback, useState } from "react";
import { JBTMemberTypeID, JBTProps } from "./types";
import { VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Dimensions, Image, Platform, Pressable } from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { TouchableOpacity } from "react-native-gesture-handler";
import JBTdropdown from "../JBTdropdown";
import { useTranslation } from "react-i18next";
import Tooltip from "@components/atoms/Tooltip";
import { VDBIcons } from "@utils/VDBIcons";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import CustomWeb from "@components/atoms/Webview";
import { Diamond } from "@src/types/DiamondDetail";
import { useSelector } from "react-redux";
import Modal from "@components/atoms/Modal";
import { useUpdateUserOnJbt } from "@apis/user";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";

const JBTDetail = ({
  logoUrl,
  vendor_name,
  member_type,
  mItem,
  jbtDetail,
  screenName,
}: JBTProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const [imagUri, setImageUri] = useState(logoUrl);
  const handleImageError = () => {
    setImageUri(FALLBACK_IMAGE_ASSET);
  };
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [showWebModal, setshowWebModal] = useState(false);
  const [showJbtWebView, setShowJbtWebView] = useState(false);
  const [jbtUrl, setJbtUrl] = useState("");
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const { deviceWidth: mWidth } = useIsFoldable();
  const mHeight = Dimensions.get("window").height;
  const [userId, setUserId] = useState(0);
  const {} = useUpdateUserOnJbt(userId);

  const key =
    member_type.toLowerCase() === "member"
      ? "productDetailPage.jbtMemberType.text"
      : "productDetailPage.jbtListed.text";

  const jbtMemberTypeIdText = () => {
    switch (userInfo.jbtDetails()?.member_type_id) {
      case JBTMemberTypeID.regularMember:
      case JBTMemberTypeID.enhancedMember:
      case JBTMemberTypeID.notSync:
        return t("productDetailPage.checkCreditReport.text");
      case JBTMemberTypeID.retailMember:
        return t("productDetailPage.checkReport.text");
      case JBTMemberTypeID.nonMember:
        return "";
      default:
        return t("productDetailPage.checkCreditReport.text");
    }
  };

  const handleTooltipToggle = useCallback(() => {
    setTooltipIsOpen((prev) => !prev);
  }, []);

  const handleOpenModel = () => {
    // setshowWebModal(true);
  };

  const getJbtUrl = (item: Diamond, requestSource: string) => {
    if (userInfo.user === undefined) {
      return null;
    }
    let mJbtUrl = new URL(process.env.EXPO_PUBLIC_JBT_URL + "/jbt/connect");

    mJbtUrl.searchParams.append("vendor_id", item?.vendor_id?.toString() ?? "");
    mJbtUrl.searchParams.append("user_token", userInfo.user.token);
    mJbtUrl.searchParams.append(
      "report_on_company_jbt_id",
      jbtDetail?.jbt_id?.toString() ?? ""
    );
    mJbtUrl.searchParams.append("p", Platform.OS);
    mJbtUrl.searchParams.append("request_source", requestSource);
    mJbtUrl.searchParams.append(
      "vendor_name",
      item?.vendor_name?.toString() ?? ""
    );
    mJbtUrl.searchParams.append("company_name", userInfo.user.company);
    mJbtUrl.searchParams.append(
      "installation_identifier",
      installationIdentifier
    );
    mJbtUrl.searchParams.append("app_version", "2.0");

    if (userInfo.user.jbt_detail && userInfo.user.jbt_detail.jbt_id > 0) {
      mJbtUrl.searchParams.append(
        "jbt_id",
        userInfo.user.jbt_detail.jbt_id?.toString()
      );
    }

    if (
      userInfo.user.jbt_detail &&
      userInfo.user.jbt_detail.member_type_id > 0
    ) {
      mJbtUrl.searchParams.append(
        "member_type_id",
        userInfo.user.jbt_detail.member_type_id?.toString()
      );
    }

    return mJbtUrl.toString();
  };

  const showWebView = () => {
    const nJbtUrl = getJbtUrl(mItem, screenName);
    if (nJbtUrl) {
      setJbtUrl(nJbtUrl);
      setShowJbtWebView(true);
    }
  };

  return (
    <ContainerView
      theme={theme}
      onPress={() => {
        showWebView();
      }}
    >
      <JBTContainer>
        <Image
          style={{
            height: 70,
            width: 70,
            resizeMode: "contain",
          }}
          source={{
            uri: imagUri,
          }}
          onError={handleImageError}
        />
        <LabelTextContainer>
          <TitleTextContainer>
            <TitleWrapContainer>
              <VDBText
                type={
                  theme.isLargeScreen
                    ? typography["Body-M"]
                    : typography["Minor-M"]
                }
                color={theme["core-05"]}
                selectable={true}
              >
                {vendor_name} {t(key)} {member_type}
              </VDBText>
            </TitleWrapContainer>
          </TitleTextContainer>

          <TitleTextContainer>
            <TouchableOpacity
              onPress={handleOpenModel}
              style={{ cursor: "pointer" }}
            >
              <TitleWrapContainer>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  color={theme["main-04"]}
                  selectable={true}
                >
                  {jbtMemberTypeIdText()}
                </VDBText>
              </TitleWrapContainer>
            </TouchableOpacity>
          </TitleTextContainer>
        </LabelTextContainer>

        <Tooltip
          isOpen={tooltipIsOpen}
          setIsOpen={setTooltipIsOpen}
          tooltipBackgroundColor={theme["over-01"]}
          trigger={(triggerProps) => (
            <Pressable {...triggerProps} onPress={handleTooltipToggle}>
              <VDBIcon
                color={theme["extra-01"]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.QUESTION}
              />
            </Pressable>
          )}
        >
          <VDBText
            color={theme["core-01"]}
            isBold={false}
            textAlign={"center"}
            selectable={true}
          >
            {t("productDetailPage.jbtToolTip.text")}
          </VDBText>
        </Tooltip>
      </JBTContainer>

      {showWebModal && (
        <JBTdropdown
          isOpen={showWebModal}
          onClose={() => setshowWebModal(false)}
          HtmlSource={{
            html: t("jbt.screenText.footer.text"),
          }}
          styleKey={"Minor-R"}
        />
      )}

      {showJbtWebView && (
        <Modal
          isOpen={showJbtWebView}
          onClose={() => setShowJbtWebView(false)}
          headerTitle={"JBT"}
          webHeight={`${mHeight}px`}
          skipKeyboardAware={true}
          isJBT={true}
        >
          <CustomWeb
            url={jbtUrl}
            {...(!theme.isLargeScreen && { height: mHeight, width: mWidth })}
            onMessage={(event: any) => {
              if (event.native.data) {
                setUserId(userInfo.user.id);
              }
            }}
          />
        </Modal>
      )}
    </ContainerView>
  );
};

const ContainerView = styled.TouchableOpacity<{ theme: any }>`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 8px;
`;

const LabelTextContainer = styled.View`
  margin-left: 4px;
  margin-right: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  flex: 1;
`;

const TitleTextContainer = styled.View`
  margin: 5px;
  display: block;
`;

const TitleWrapContainer = styled.View`
  justify-content: space-between;
  align-items: flex-start;
  margin-left: 4px;
  flex-wrap: wrap;
`;

const JBTContainer = styled.View`
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

export default JBTDetail;
